import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { PLATFORM_MARK } from "@/components/PlatformLogo";
import {
  LayoutDashboard, MessageSquare, Users, FolderKanban, FileText,
  CalendarClock, BellRing, Settings, MessageSquareCode, LogOut, Menu, X, Wifi, LifeBuoy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";
import NextmindAssistant from "@/components/NextmindAssistant";
import GuidedTour from "@/components/GuidedTour";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/inbox", key: "inbox", icon: MessageSquare, testid: "nav-inbox" },
  { to: "/clienti", key: "clienti", icon: Users, testid: "nav-clienti" },
  { to: "/pratiche", key: "pratiche", icon: FolderKanban, testid: "nav-pratiche" },
  { to: "/preventivi", key: "preventivi", icon: FileText, testid: "nav-preventivi" },
  { to: "/sopralluoghi", key: "sopralluoghi", icon: CalendarClock, testid: "nav-sopralluoghi" },
  { to: "/promemoria", key: "promemoria", icon: BellRing, testid: "nav-promemoria" },
  { to: "/assistenza", key: "assistenza", icon: LifeBuoy, testid: "nav-assistenza" },
  { to: "/impostazioni", key: "impostazioni", icon: Settings, testid: "nav-impostazioni" },
];
const MOBILE_NAV = NAV.slice(0, 4);

function Brand({ small }) {
  const { branding } = useAuth();
  const name = branding?.app_name || "NEXTMIND INFISSI";
  const sub = branding?.app_subtitle || "Preventivi AI";
  return (
    <div className="flex items-center gap-2.5">
      <img src={branding?.logoAbs || PLATFORM_MARK} alt={name} className="w-9 h-9 rounded-lg object-contain bg-white/5" />
      {!small && (
        <div className="leading-tight">
          <div className="font-heading font-extrabold text-white tracking-tight text-lg">{name}</div>
          <div className="text-[10px] text-sky-300 uppercase tracking-widest">{sub}</div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const sidebar = (
    <div className="flex flex-col h-full bg-[#0F172A] text-slate-300 w-[260px]">
      <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
        <Brand />
        <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
      </div>
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 bg-emerald-500/10 text-emerald-300 rounded-lg px-3 py-2 text-xs font-medium">
          <Wifi className="w-4 h-4" /> Assistente AI · WhatsApp TEST
        </div>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto scrollbar-thin">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-white text-[#0F172A]" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
            <n.icon className="w-[18px] h-[18px]" /> {t(`nav.${n.key}`)}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-2 text-xs text-slate-400">
          <div className="font-semibold text-slate-200">{user?.name}</div>
          <div className="uppercase tracking-wide text-[10px]">{user?.role}</div>
        </div>
        <button data-testid="repeat-tour-btn" onClick={() => window.dispatchEvent(new Event("nm-start-tour"))}
          className="w-full flex items-center gap-2 px-3 py-2 mb-1 rounded-lg text-xs text-slate-400 hover:bg-white/10 hover:text-white">
          <LifeBuoy className="w-4 h-4" /> {t("tour.repeat")}
        </button>
        <Button data-testid="logout-btn" variant="ghost" onClick={() => { logout(); nav("/login"); }}
          className="w-full justify-start text-slate-300 hover:bg-white/10 hover:text-white">
          <LogOut className="w-4 h-4 mr-2" /> {t("common.logout")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <aside className="hidden lg:block fixed inset-y-0 left-0 z-30">{sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0">{sidebar}</div>
        </div>
      )}
      <div className="flex-1 lg:ml-[260px] flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 h-14 flex items-center px-4 gap-3">
          <button data-testid="mobile-menu-btn" className="lg:hidden text-slate-700" onClick={() => setOpen(true)}><Menu className="w-6 h-6" /></button>
          <div className="lg:hidden font-heading font-extrabold text-[#0F172A]">{useAuth().branding?.app_name || "NEXTMIND INFISSI"}</div>
          <div className="ml-auto flex items-center gap-2">
            <LanguageSelector variant="light" persist />
            <NavLink to="/simulatore" data-testid="nav-simulatore" className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-3 py-1.5 hover:bg-sky-100">
              <MessageSquareCode className="w-4 h-4" /> {t("nav.simulatore")}
            </NavLink>
            <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-xs font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 min-w-0 pb-24 lg:pb-8">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 h-16 flex safe-bottom">
        {MOBILE_NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottom-${n.testid}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${isActive ? "text-sky-600" : "text-slate-500"}`}>
            <n.icon className="w-5 h-5" /> {t(`nav.${n.key}`)}
          </NavLink>
        ))}
        <NavLink to="/impostazioni" data-testid="bottom-nav-altro"
          className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${isActive ? "text-sky-600" : "text-slate-500"}`}>
          <Settings className="w-5 h-5" /> {t("nav.altro")}
        </NavLink>
      </nav>
      <NextmindAssistant />
      <GuidedTour />
    </div>
  );
}
